import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { I18N_KEYS } from '@/language/keys';
import { log } from '@/logger/logger';
import { useGlobalStore } from '@/state/global.state';
import { WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES } from '@/consts/analytics.consts';
import { TrackTypeEnum, useAnalytics } from '@/hooks/useAnalytics';
import type { WorkflowCommunityPublishLinkTrackingContext } from './workflowCommunityPublishAnalytics';

const logger = log.getLogger('useCommunityLinkActions');

export const useCommunityLinkActions = (
  communityUrl: string | null,
  trackingContext?: WorkflowCommunityPublishLinkTrackingContext,
) => {
  const { t } = useTranslation();
  const updateSnackbarData = useGlobalStore((s) => s.updateSnackbarData);
  const { track } = useAnalytics();

  const trackLinkAction = useCallback(
    (action: 'copy_link' | 'view_in_community') => {
      if (!trackingContext) return;
      track(
        WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.LINK_ACTION_CLICKED,
        {
          ...trackingContext,
          action,
        },
        TrackTypeEnum.Product,
      );
    },
    [track, trackingContext],
  );

  const copyLink = useCallback(async () => {
    if (!communityUrl) return;
    trackLinkAction('copy_link');
    try {
      await navigator.clipboard.writeText(communityUrl);
      updateSnackbarData({
        text: t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.SNACKBAR.LINK_COPIED),
        isOpen: true,
      });
    } catch (error) {
      logger.error('Failed to copy community link', error);
    }
  }, [communityUrl, trackLinkAction, updateSnackbarData, t]);

  const goToCommunity = useCallback(() => {
    if (!communityUrl) return;
    trackLinkAction('view_in_community');
    window.open(communityUrl, '_blank', 'noopener,noreferrer');
  }, [communityUrl, trackLinkAction]);

  return { copyLink, goToCommunity };
};
